/**
 * WebSocket Authentication Tracker
 *
 * Prevents rapid repeated authentication attempts with the same token.
 * This protects against auth spam when authentication fails or connection issues occur.
 *
 * The tracker enforces a 5-second throttle - once a token is attempted,
 * it cannot be retried for 5 seconds. This prevents:
 * - Rapid retries of failed authentication
 * - Accidental auth spam from reconnection logic
 * - Server overload from repeated auth messages
 */

const authAttempts = new Map<string, number>(); // token -> timestamp
const AUTH_THROTTLE_MS = 5000; // 5 seconds

/**
 * Check if authentication can be attempted with this token
 */
export function canAuthenticate(token: string): boolean {
  const lastAttempt = authAttempts.get(token);
  if (!lastAttempt) {
    return true; // Never attempted, can proceed
  }

  const timeSince = Date.now() - lastAttempt;
  return timeSince >= AUTH_THROTTLE_MS;
}

/**
 * Mark that authentication was attempted with this token
 */
export function markAuthAttempt(token: string): void {
  authAttempts.set(token, Date.now());
}

/**
 * Clear all tracked authentication attempts (call on logout)
 */
export function clearAuthAttempts(): void {
  authAttempts.clear();
}

/**
 * Get count of tracked auth attempts (for debugging/monitoring)
 */
export function getAuthAttemptCount(): number {
  return authAttempts.size;
}
